import type { Anmerkung } from '@awf/shared';
import { MAX_KOMMENTAR_ZEICHEN } from '@awf/shared';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { bildlaufVerhalten } from '../ui/bewegung.js';
import { klasse } from '../ui/klasse.js';
import { LinienSymbol } from '../ui/linien-symbol.js';
import { istErlaubteBildquelle } from './als-html.js';
import { kurzesZitat } from './anmerkungen.js';
import { useBildLupe } from './bild-lupe.js';
import stile from './rueckmelde-spalte.module.css';

export interface SpaltenBild {
  index: number;
  name: string;
  feedback: string;
  /**
   * Drive-Adresse des Vorschaubilds (`TextAntwort.bilder[].vorschauUrl`), Spec §3.3
   * "Vorschaubild, Name, Feedback-Feld". Optional, weil Laeufe von vor diesem Feld (und die
   * Bilder-Kette ohne Text) es nicht mitbringen -- dann steht in der Spalte kein Bild, nur
   * Name und Feedback-Feld (Ruling R13-4).
   */
  vorschauUrl?: string;
}

export interface RueckmeldeSpalteProps {
  gesamtFeedback: string;
  onGesamtFeedback(text: string): void;
  gesamtFeedbackGesperrt: boolean;
  anmerkungen: readonly Anmerkung[];
  ungueltig: ReadonlySet<Anmerkung>;
  hervorgehoben: Anmerkung | null;
  /**
   * Zaehlt jeden Treffer auf eine eingefaerbte Stelle. Ein zweiter Klick auf dieselbe Stelle setzt
   * dieselbe Anmerkung, die Identitaet allein loeste den Bildlauf also nicht erneut aus.
   */
  hervorhebungsAnlass: number;
  anmerkungenHinweis: ReactNode;
  onZurStelle(anmerkung: Anmerkung): void;
  onAnmerkungLoeschen(anmerkung: Anmerkung): void;
  bilder: readonly SpaltenBild[];
  /** Zum Bild springen und dort das Feld oeffnen: Geschrieben wird nur am Bild. */
  onZumBild(index: number): void;
  onBildZurueck(index: number): void;
  textAenderungen: string;
  /** Offene `[Prüfhinweis: …]` im aktuellen Text samt Entwurf (Spec 2026-10-12 §5.5); 0: kein Abschnitt. */
  pruefhinweise: number;
  entwurfFehler: boolean;
  loeschenFehler: boolean;
  meldungen: ReactNode;
  hauptknopf: { text: string; deaktiviert: boolean };
  deaktiviert: boolean;
}

// Aus freigabe-page.tsx hierher verschoben (Task 14 entfernt sie dort).
const ENTWURF_FEHLERTEXT =
  'Zwischenstand konnte nicht gespeichert werden. Ihre Bearbeitung bleibt erhalten.';
const ENTWURF_LOESCHEN_FEHLERTEXT =
  'Die Fassung der Content Engine konnte nicht geladen werden. Ihre Bearbeitung bleibt bestehen.';

/**
 * Vorschaubild als Lupen-Knopf (Spec 2026-09-30-lupe-bilder §4.3). Laedt es nicht, verschwindet
 * es samt Knopf -- dann gibt es keine Lupe. Der Aufrufer setzt `key={vorschau}`, damit eine neue
 * Adresse den Ladefehler vergisst (Review Focus 3).
 */
function SpaltenVorschau({ vorschau, name }: { vorschau: string; name: string }) {
  const { oeffnen } = useBildLupe();
  const [laedtNicht, setLaedtNicht] = useState(false);
  if (laedtNicht) return null;
  return (
    <button
      type="button"
      className={stile.vorschauKnopf}
      // R13-5: Am Bild selbst steht ein gleichnamiger Knopf "{name} vergrößern".
      aria-label={`${name} vergrößern (Rückmeldespalte)`}
      onClick={() => oeffnen({ vorschau, name })}
    >
      <img className={stile.vorschau} src={vorschau} alt="" onError={() => setLaedtNicht(true)} />
      <span className={stile.lupenMarke}>
        <LinienSymbol name="maximize" groesse={14} />
      </span>
    </button>
  );
}

/** Alles, was mit dem Absenden hinausgeht, an einem Ort (Spec §3). */
export function RueckmeldeSpalte(props: RueckmeldeSpalteProps) {
  const hervorgehobenRef = useRef<HTMLLIElement>(null);
  // M9: ein stabiler Schluessel je Anmerkung. Mit `key={i}` gab React beim Loeschen eines Eintrags
  // dessen Knoten an den naechsten weiter -- Fokus und Hervorhebung wanderten mit. Anmerkungen
  // haben keine eigene Kennung; die Seite ersetzt sie nie, sie legt neue an oder entfernt sie.
  // Deshalb genuegt ihre Objektidentitaet (WeakMap, damit geloeschte nicht liegen bleiben); steht
  // dasselbe Objekt mehrfach in der Liste, zaehlt das Vorkommen mit.
  const schluessel = useRef(new WeakMap<Anmerkung, number>());
  const naechsterSchluessel = useRef(0);
  const schluesselVon = (anmerkung: Anmerkung, vorkommen: Map<Anmerkung, number>): string => {
    let wert = schluessel.current.get(anmerkung);
    if (wert === undefined) {
      naechsterSchluessel.current += 1;
      wert = naechsterSchluessel.current;
      schluessel.current.set(anmerkung, wert);
    }
    const zuvor = vorkommen.get(anmerkung) ?? 0;
    vorkommen.set(anmerkung, zuvor + 1);
    return `${wert}-${zuvor}`;
  };
  // Je Render neu: zaehlt, wie oft dasselbe Objekt schon vorkam.
  const vorkommen = new Map<Anmerkung, number>();
  useEffect(() => {
    // jsdom kennt `scrollIntoView` nicht.
    if (props.hervorgehoben && typeof hervorgehobenRef.current?.scrollIntoView === 'function') {
      hervorgehobenRef.current.scrollIntoView({ block: 'nearest', behavior: bildlaufVerhalten() });
    }
  }, [props.hervorgehoben, props.hervorhebungsAnlass]);

  return (
    <aside id="rueckmeldung" className={stile.spalte} aria-label="Rückmeldung">
      <section className={stile.teil}>
        <label className={stile.titel} htmlFor="gesamtfeedback">
          Gesamtfeedback
        </label>
        <textarea
          id="gesamtfeedback"
          className={klasse('eds-field__input', stile.eingabe)}
          value={props.gesamtFeedback}
          maxLength={MAX_KOMMENTAR_ZEICHEN}
          disabled={props.deaktiviert || props.gesamtFeedbackGesperrt}
          onChange={(e) => props.onGesamtFeedback(e.target.value)}
        />
        {props.gesamtFeedbackGesperrt && (
          <p className={stile.leise}>
            Das Rundenlimit ist erreicht, der Text kann nur noch freigegeben werden.
          </p>
        )}
      </section>

      <section className={stile.teil} aria-labelledby="anmerkungen-titel">
        <h2 id="anmerkungen-titel" className={stile.titel}>
          Anmerkungen ({props.anmerkungen.length})
        </h2>
        {props.anmerkungen.length === 0 ? (
          <p className={stile.leise}>{props.anmerkungenHinweis}</p>
        ) : (
          <ul className={stile.liste} aria-label="Anmerkungen">
            {props.anmerkungen.map((anmerkung, i) => {
              const istUngueltig = props.ungueltig.has(anmerkung);
              const istHervorgehoben = props.hervorgehoben === anmerkung;
              return (
                <li
                  key={schluesselVon(anmerkung, vorkommen)}
                  ref={istHervorgehoben ? hervorgehobenRef : undefined}
                  className={stile.eintrag}
                  aria-current={istHervorgehoben ? 'true' : undefined}
                  data-ungueltig={istUngueltig ? 'true' : undefined}
                >
                  <button
                    type="button"
                    className={stile.zitat}
                    title="Zur markierten Stelle springen"
                    // Das Zitat kann 2000 Zeichen lang sein -- als Name zu viel.
                    aria-label={`„${kurzesZitat(anmerkung.anker.zitat)}“`}
                    onClick={() => props.onZurStelle(anmerkung)}
                  >
                    „{anmerkung.anker.zitat}“
                  </button>
                  <p className={stile.kommentar}>{anmerkung.kommentar}</p>
                  {istUngueltig && (
                    <p className={stile.warnung}>
                      Diese Anmerkung zeigt auf keine Textstelle mehr. Bitte löschen oder neu
                      setzen.
                    </p>
                  )}
                  <div className={stile.aktionen}>
                    {/* Eigene, sichtbare Aktion (Spec §3): Das Zitat allein verraet nicht, dass
                        es ein Sprungziel ist. Bei einer ungueltigen Anmerkung gibt es keine
                        Stelle mehr, zu der man springen koennte. */}
                    <button
                      type="button"
                      className="eds-button eds-button--tertiary eds-button--sm"
                      aria-label={`Zur Textstelle: „${kurzesZitat(anmerkung.anker.zitat)}“`}
                      disabled={istUngueltig}
                      onClick={() => props.onZurStelle(anmerkung)}
                    >
                      Zur Textstelle
                    </button>
                    <button
                      type="button"
                      className="eds-button eds-button--tertiary eds-button--sm"
                      aria-label={`Anmerkung ${i + 1} löschen`}
                      disabled={props.deaktiviert}
                      onClick={() => props.onAnmerkungLoeschen(anmerkung)}
                    >
                      Löschen
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {props.pruefhinweise > 0 && (
        <section className={stile.teil} aria-labelledby="pruefhinweise-titel">
          <h2 id="pruefhinweise-titel" className={stile.titel}>
            Prüfhinweise ({props.pruefhinweise})
          </h2>
          <p className={stile.leise}>
            Im Text stehen noch offene Prüfhinweise. Bei der Freigabe werden sie entfernt.
          </p>
        </section>
      )}

      {props.bilder.length > 0 && (
        <section className={stile.teil} aria-labelledby="bilder-titel">
          <h2 id="bilder-titel" className={stile.titel}>
            Bilder zur Optimierung ({props.bilder.length})
          </h2>
          <ul className={stile.liste} aria-label="Bilder zur Optimierung">
            {props.bilder.map((bild) => (
              <li key={bild.index} className={stile.eintrag}>
                {bild.vorschauUrl && istErlaubteBildquelle(bild.vorschauUrl) && (
                  <SpaltenVorschau
                    key={bild.vorschauUrl}
                    vorschau={bild.vorschauUrl}
                    name={bild.name}
                  />
                )}
                <p className={stile.bildName}>{bild.name}</p>
                {/* Nur Anzeige, wie bei den Anmerkungen (Rueckmeldung 2026-10-16): Mit einem
                    zweiten Feld hier gab man links und rechts Feedback und wusste nicht, wo es
                    zaehlt. Geschrieben wird am Bild; "Zum Bild" fuehrt dorthin. */}
                {bild.feedback.trim() ? (
                  <p className={stile.kommentar}>{bild.feedback}</p>
                ) : (
                  <p className={stile.leise}>
                    Noch keine Rückmeldung. Beschreiben Sie am Bild, was anders werden soll.
                  </p>
                )}
                <div className={stile.aktionen}>
                  <button
                    type="button"
                    className="eds-button eds-button--tertiary eds-button--sm"
                    aria-label={`Zum Bild: ${bild.name}`}
                    disabled={props.deaktiviert}
                    onClick={() => props.onZumBild(bild.index)}
                  >
                    Zum Bild
                  </button>
                  <button
                    type="button"
                    className="eds-button eds-button--tertiary eds-button--sm"
                    aria-label={`${bild.name} zurück auf Passt`}
                    disabled={props.deaktiviert}
                    onClick={() => props.onBildZurueck(bild.index)}
                  >
                    Zurück auf Passt
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className={stile.teil}>
        <h2 className={stile.titel}>Textänderungen</h2>
        <p className={stile.leise}>{props.textAenderungen}</p>
      </section>

      {/* Kein eigener Block "Zwischenstand" mehr (Rueckmeldung 2026-10-13): Gesichert wird still,
          die Fassungsauswahl steht im Seitenkopf. Scheitert das Sichern oder Verwerfen, steht die
          Meldung hier ueber dem Hauptknopf. */}
      <div className={stile.abschluss}>
        {props.entwurfFehler && (
          <p className="eds-field__hint" role="alert">
            {ENTWURF_FEHLERTEXT}
          </p>
        )}
        {props.loeschenFehler && (
          <p className="eds-field__hint" role="alert">
            {ENTWURF_LOESCHEN_FEHLERTEXT}
          </p>
        )}
        {props.meldungen}
        <button
          className="eds-button eds-button--primary"
          type="submit"
          disabled={props.hauptknopf.deaktiviert}
        >
          {props.hauptknopf.text}
        </button>
      </div>
    </aside>
  );
}
